'use client'

import { motion } from 'framer-motion'
import { useEffect, useState, useCallback } from 'react'
import Link from 'next/link'
import SectionTitle from '@/components/ui/SectionTitle'
import Image from '@/components/ui/MediaImage'
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext'
import { getBackendRenderAttributes, isBackendRenderMode } from '@/lib/backend-render-utils'
import { transformMediaUrl } from '@/lib/media-url-transformer'
import { UrlBuilder } from '@/lib/url-builder'
import type { PageUrlMap } from '@/types/blog'

interface MiceProduct {
  id: number
  name: string
  slug: string
  type: string
  cover_image?: string
  cover_image_alt?: string
  description?: string
  metas?: Record<string, unknown>
  gallery?: Array<{ url?: string; src?: string; type?: string }>
}

interface MiceProductGridProps {
  title?: string
  subTitle?: string
  description?: string
  limit?: number
  products?: MiceProduct[]
  languageId?: number
  language?: string
  languageCode?: string
  pageUrls?: PageUrlMap
  defaultLanguage?: string
  enableClientFetch?: boolean
  __pageContext?: {
    languageId?: number
    language?: string
    languageCode?: string
    pageUrls?: PageUrlMap
  }
  __grapesPreview?: boolean
}

function parseLocation(metas?: Record<string, unknown> | null): string[] {
  if (!metas?.location) return []
  const loc = metas.location
  if (typeof loc === 'string') {
    try {
      const parsed = JSON.parse(loc)
      if (Array.isArray(parsed)) return parsed.map((l: unknown) => String(l)).filter(Boolean)
      return [String(parsed)].filter(Boolean)
    } catch {
      return [loc].filter(Boolean)
    }
  }
  if (Array.isArray(loc)) return loc.map((l: unknown) => String(l)).filter(Boolean)
  return []
}

export default function MiceProductGrid({
  title: propTitle,
  subTitle: propSubTitle,
  description: propDescription,
  limit: propLimit = 4,
  products: initialProducts,
  languageId: propLanguageId,
  language: propLanguage,
  languageCode: propLanguageCode,
  pageUrls: propPageUrls,
  defaultLanguage: propDefaultLanguage = 'en',
  enableClientFetch = true,
  __pageContext,
  __grapesPreview = false,
  ...restProps
}: MiceProductGridProps = {}) {
  const [products, setProducts] = useState<MiceProduct[]>(initialProducts || [])
  const [loading, setLoading] = useState(!initialProducts?.length)
  const [error, setError] = useState<string | null>(null)

  const languageId = propLanguageId ?? __pageContext?.languageId
  const resolvedLanguageCode = propLanguage || propLanguageCode || __pageContext?.language || __pageContext?.languageCode
  const pageUrls = propPageUrls || __pageContext?.pageUrls
  const defaultLanguage = propDefaultLanguage

  const { getVariable, language: contextLanguage } = useLanguageVariables()
  const languageCodeForApi = (resolvedLanguageCode || contextLanguage || defaultLanguage || 'tr').toLowerCase()

  const isBackendRender = isBackendRenderMode({ __grapesPreview, ...restProps })
  const backendAttributes = getBackendRenderAttributes(
    'sections/MiceProductGrid',
    {
      limit: propLimit,
      languageId: propLanguageId,
      language: propLanguage,
      languageCode: propLanguageCode,
      pageUrls: propPageUrls,
      enableClientFetch,
    },
    isBackendRender
  )

  const resolveImages = useCallback(
    (product: MiceProduct): { mainUrl: string; overlayUrl: string | null } => {
      const gallery = product.gallery
      const cover = product.cover_image
      let mainUrl = ''
      let overlayUrl: string | null = null

      if (Array.isArray(gallery) && gallery.length > 0) {
        const images = gallery.filter((g) => g.type === 'image' || !g.type)
        const first = images[0]
        const second = images[1]
        if (first) mainUrl = transformMediaUrl(first.url || first.src || '') || ''
        if (second) overlayUrl = transformMediaUrl(second.url || second.src || '') || null
      }
      if (!mainUrl && cover) mainUrl = transformMediaUrl(cover) || ''
      return { mainUrl, overlayUrl }
    },
    []
  )

  const buildProductUrl = useCallback(
    (product: MiceProduct): string => {
      if (!pageUrls || !resolvedLanguageCode) return '#'
      const builder = new UrlBuilder(pageUrls, resolvedLanguageCode, defaultLanguage)
      return builder.buildProductDetailUrl('mice', product.slug)
    },
    [pageUrls, resolvedLanguageCode, defaultLanguage]
  )

  useEffect(() => {
    const hasInitialData = !!(initialProducts && initialProducts.length > 0)
    if (hasInitialData) {
      setProducts(initialProducts!)
    }

    if (hasInitialData && !enableClientFetch) {
      setLoading(false)
      return
    }

    if (!enableClientFetch) {
      setLoading(false)
      return
    }

    let cancelled = false

    async function fetchProducts() {
      try {
        if (process.env.NODE_ENV === 'development' && !hasInitialData) {
          console.debug('[MiceProductGrid] client /api/products fetch (no SSR initialProducts)')
        }
        if (!hasInitialData) setLoading(true)
        setError(null)
        const params = new URLSearchParams({
          type: 'mice',
          limit: String(propLimit),
          language: languageCodeForApi,
        })
        if (languageId != null) {
          params.set('language_id', String(languageId))
        }

        const response = await fetch(`/api/products?${params.toString()}`)
        if (!response.ok) throw new Error(`API error: ${response.status}`)
        const data = await response.json()
        const items = Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : []
        if (!cancelled) {
          setProducts(items)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unknown error')
          if (!hasInitialData) setProducts([])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void fetchProducts()
    return () => {
      cancelled = true
    }
  }, [enableClientFetch, languageId, languageCodeForApi, propLimit, initialProducts])

  const title = propTitle || getVariable('mice_product_grid_title', 'MICE Hizmetleri')
  const subTitle = propSubTitle || getVariable('mice_product_grid_subtitle', 'MICE')
  const description =
    propDescription ||
    getVariable('mice_product_grid_description', 'Meetings, Incentives, Conferences & Exhibitions hizmetlerimizi keşfedin.')
  /** API/DB'de anahtar yoksa son fallback dil bazlı olsun (yalnızca 'detay' kullanınca EN sitede yanlış görünüyordu). */
  const detailFallback =
    (resolvedLanguageCode || contextLanguage || defaultLanguage).toLowerCase() === 'en' ? 'detail' : 'detay'
  const detailLabel = getVariable(
    'mice_product_grid_detail',
    getVariable('detail', detailFallback)
  )

  if (loading) {
    const skeletonCount = Math.min(4, propLimit)
    return (
      <section {...backendAttributes} className="mx-auto w-full min-w-0 max-w-9/12 px-4 sm:px-6">
        <SectionTitle title={title} titleTag="h2" subTitle={subTitle} subTitleTag="span" description={description} descriptionTag="p" />
        <div className="mt-16 space-y-[140px] md:space-y-[190px]">
          {Array.from({ length: skeletonCount }).map((_, i) => {
            const reverse = i % 2 === 1
            const collage = (
              <div className="flex-1 flex justify-center lg:justify-start min-w-0 w-full max-w-[660px] mx-auto lg:mx-0">
                <div className="relative w-full max-w-[660px] aspect-[660/491] shrink-0">
                  <div
                    className="absolute left-0 top-0 z-10 w-[calc(390*100%/660)] h-[calc(441*100%/491)] rounded-lg bg-gradient-to-br from-gray-200 to-gray-100 animate-pulse"
                    aria-hidden
                  />
                  <div
                    className="absolute left-1/2 top-[calc(191*100%/491)] z-20 w-1/2 h-[calc(300*100%/491)] rounded-lg bg-gradient-to-br from-gray-300 to-gray-200 shadow-md animate-pulse"
                    aria-hidden
                  />
                </div>
              </div>
            )
            const textBlock = (
              <div className="flex-1 flex flex-col gap-6 min-w-0 w-full">
                <div className="h-9 lg:h-10 bg-gray-200 rounded-md animate-pulse w-[85%] max-w-md" aria-hidden />
                <div className="h-4 bg-gray-100 rounded animate-pulse w-2/5 max-w-xs" aria-hidden />
                <div className="space-y-2.5" aria-hidden>
                  <div className="h-3.5 bg-gray-100 rounded animate-pulse w-full" />
                  <div className="h-3.5 bg-gray-100 rounded animate-pulse w-[92%]" />
                  <div className="h-3.5 bg-gray-100 rounded animate-pulse w-[70%]" />
                </div>
                <div className="h-10 w-32 bg-gray-200 rounded-lg animate-pulse" aria-hidden />
              </div>
            )
            return (
              <div
                key={i}
                className="flex flex-col lg:flex-row gap-10 lg:gap-14 items-center w-full"
              >
                {reverse ? (
                  <>
                    {textBlock}
                    {collage}
                  </>
                ) : (
                  <>
                    {collage}
                    {textBlock}
                  </>
                )}
              </div>
            )
          })}
        </div>
      </section>
    )
  }

  if (error) {
    return (
      <section {...backendAttributes} className="mx-auto w-full min-w-0 max-w-9/12 px-4 py-12 sm:px-6">
        <div className="text-center text-red-500" role="alert">
          {getVariable('mice_product_grid_load_error', 'MICE ürünleri yüklenemedi')}: {error}
        </div>
      </section>
    )
  }

  if (!products.length) {
    return (
      <section {...backendAttributes} className="mx-auto w-full min-w-0 max-w-9/12 px-4 py-12 sm:px-6">
        <div className="text-center text-alsero-light-gray">
          {getVariable('mice_product_grid_no_products', 'Henüz MICE hizmeti bulunmuyor.')}
        </div>
      </section>
    )
  }

  return (
    <motion.section
      {...backendAttributes}
      className="relative mx-auto mt-12 w-full min-w-0 max-w-9/12 px-4 sm:mt-16 sm:px-6 md:mt-[70px]"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
    >
      <SectionTitle title={title} titleTag="h2" subTitle={subTitle} subTitleTag="span" description={description} descriptionTag="p" />

      <div className="mt-16 space-y-[140px] md:space-y-[190px]">
        {products.map((product, index) => {
          const { mainUrl, overlayUrl } = resolveImages(product)
          const href = buildProductUrl(product)
          const locations = parseLocation(product.metas ?? undefined)
          const reverse = index % 2 === 1
          const rawDesc =
            (typeof product.description === 'string' ? product.description.replace(/<[^>]*>/g, '').trim() : '')
          const desc = rawDesc.length > 250 ? rawDesc.slice(0, 250) + '...' : rawDesc

          const contentBlock = (
            <motion.div
              className="flex-1 flex flex-col gap-6"
              initial={{ opacity: 0, x: reverse ? 50 : -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ type: 'spring', stiffness: 80, damping: 20, delay: 0.1 }}
            >
              <h3 className="text-2xl lg:text-3xl xl:text-4xl font-bold text-alsero-dark-gray leading-snug">
                {product.name}
              </h3>
              {locations.length > 0 && (
                <p className="text-sm font-medium text-alsero-first">
                  {locations.join(' - ')}
                </p>
              )}
              {desc && (
                <p className="text-alsero-text-tour text-base lg:text-lg leading-relaxed line-clamp-4">
                  {desc}
                </p>
              )}
              <Link
                href={href}
                className="inline-flex items-center gap-2 w-fit px-5 py-2.5 bg-alsero-first text-white rounded-lg font-medium hover:bg-alsero-first/90 transition-colors text-sm"
                aria-label={`${product.name} - ${detailLabel}`}
              >
                {detailLabel}
                <span aria-hidden>↗</span>
              </Link>
            </motion.div>
          )

          /** Kolaj: ana 390×441, üst üste 330×300; yatay 60px bindirme, ikinci 50px aşağıda (tasarım ölçüleri). */
          const imagesBlock = (
            <motion.div
              className="flex-1 flex flex-col min-w-0 justify-center"
              initial={{ opacity: 0, x: reverse ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ type: 'spring', stiffness: 80, damping: 20, delay: 0.2 }}
            >
              <div
                className={
                  overlayUrl
                    ? 'relative w-full max-w-[660px] aspect-[660/491] mx-auto lg:mx-0'
                    : 'relative w-full max-w-[390px] aspect-[390/441] mx-auto lg:mx-0'
                }
              >
                {mainUrl ? (
                  <div
                    className={
                      overlayUrl
                        ? 'absolute left-0 top-0 z-10 w-[calc(390*100%/660)] h-[calc(441*100%/491)] rounded-lg overflow-hidden'
                        : 'absolute inset-0 rounded-lg overflow-hidden'
                    }
                  >
                    <Image
                      src={mainUrl}
                      alt={product.cover_image_alt || product.name}
                      width={390}
                      height={441}
                      className="w-full h-full object-cover rounded-lg"
                      unoptimized={mainUrl.startsWith('/api/media/proxy/')}
                    />
                  </div>
                ) : (
                  <div
                    className={
                      overlayUrl
                        ? 'absolute left-0 top-0 z-10 w-[calc(390*100%/660)] h-[calc(441*100%/491)] rounded-lg bg-gray-100 flex items-center justify-center text-alsero-light-gray text-sm'
                        : 'absolute inset-0 rounded-lg bg-gray-100 flex items-center justify-center text-alsero-light-gray text-sm'
                    }
                    aria-hidden
                  >
                    {product.name}
                  </div>
                )}
                {overlayUrl && (
                  <div className="absolute left-1/2 top-[calc(191*100%/491)] z-20 w-1/2 h-[calc(300*100%/491)] rounded-lg overflow-hidden shadow-lg">
                    <Image
                      src={overlayUrl}
                      alt=""
                      width={330}
                      height={300}
                      className="w-full h-full object-cover rounded-lg"
                      unoptimized={overlayUrl.startsWith('/api/media/proxy/')}
                    />
                  </div>
                )}
              </div>
            </motion.div>
          )

          return (
            <motion.div
              key={product.id}
              className="flex flex-col lg:flex-row gap-10 lg:gap-14 items-center"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-80px' }}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: { staggerChildren: 0.1 },
                },
              }}
            >
              {reverse ? (
                <>
                  {contentBlock}
                  {imagesBlock}
                </>
              ) : (
                <>
                  {imagesBlock}
                  {contentBlock}
                </>
              )}
            </motion.div>
          )
        })}
      </div>
    </motion.section>
  )
}
